import {AxiosResponse} from 'axios';
import {sortBy} from 'lodash';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {getCustodians} from '../../api/EcaServices';
import {CustodiansQueryConditionType} from '../../components/pages/index-search/CustodiansDropdown';
import {RootState} from '../../redux/store';

export interface CustodiansResponse {
  data: {
    uniqueCustodians: number;
    totalDocumentsCount: number;
    custodian: CustodianModal[];
  };
}

export interface CustodianModal {
  custodianId: string;
  name: string;
  documentCount: number;
}

export interface UseCastodiansReturn {
  isLoading?: boolean;
  custodians: CustodianModal[];
  selectedCustodians: CustodianModal[];
  queryConditionType: CustodiansQueryConditionType;
  setSelectedCustodians: React.Dispatch<React.SetStateAction<CustodianModal[]>>;
  setQueryConditionType: React.Dispatch<
    React.SetStateAction<CustodiansQueryConditionType>
  >;
  onSearch: (searchTerm: string) => Promise<CustodianModal[] | undefined>;
}

export const useCustodians = (
  params: Record<string, string | number> = {}
): UseCastodiansReturn => {
  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;

  const projectId = useSelector((state: RootState) => state.reports).projectId;

  const custodiansAll = useRef<CustodianModal[]>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [custodians, setCustodians] = useState<CustodianModal[]>([]);
  const [selectedCustodians, setSelectedCustodians] = useState<
    CustodianModal[]
  >([]);
  const [queryConditionType, setQueryConditionType] =
    useState<CustodiansQueryConditionType>('OR');

  const sortCustodiansByLastName = (
    custodians: CustodianModal[]
  ): CustodianModal[] => {
    return sortBy(custodians, (custodian) => {
      // Extracting last name from the full name
      const lastName = custodian.name.split(' ');
      return lastName.length > 0 ? lastName[0].toLowerCase() : custodian.name; // Sorting by the last name in lowercase for consistency
    });
  };

  const getCustodiansFromServer = useCallback(() => {
    setIsLoading(true);
    getCustodians(clientId, projectId ?? '', params)
      .then(({data, status}: AxiosResponse<CustodiansResponse>) => {
        if (status === 200) {
          let custodian = sortCustodiansByLastName(data.data.custodian);
          custodian = custodian.map((item, index) => ({
            ...item,
            key: `${item.name}${index}`,
          }));
          custodiansAll.current = custodian;
          setCustodians(custodian);
          setIsLoading(false);
        }
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, [clientId, projectId]);

  useEffect(() => {
    getCustodiansFromServer();
  }, [getCustodiansFromServer]);

  const onSearch = useCallback(
    async (searchTerm: string): Promise<CustodianModal[] | undefined> => {
      try {
        const results = custodiansAll.current?.filter(
          ({name}) =>
            typeof name === 'string' &&
            name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        return results;
      } catch (e) {
        return [];
      }
    },
    [custodiansAll]
  );

  return {
    isLoading,
    custodians: custodians,
    selectedCustodians: selectedCustodians,
    queryConditionType: queryConditionType,
    setSelectedCustodians,
    setQueryConditionType,
    onSearch,
  };
};
